import React, {useCallback, useContext, useEffect} from "react"
import {useTranslation} from "react-i18next"
import {useLocation, useNavigate} from "react-router-dom"

import Avatar from "@mui/material/Avatar"
import Box from "@mui/material/Box"
import Button from "@mui/material/Button"
import Card from "@mui/material/Card"
import CssBaseline from "@mui/material/CssBaseline"
import MuiDrawer from "@mui/material/Drawer"
import Grid from "@mui/material/Grid2"
import {CSSObject, styled, Theme} from "@mui/material/styles"

import {useKeycloakLogout} from "@auth/hooks/useKeycloakLogout"

import {ChaneRoleBoradCast, logOut} from "@components/common/BoradCastChanel"
import DropDownMenu from "@components/common/DropDownMenu"
import DropDownMenuLight from "@components/common/DropDownMenuLight"
import {
    AccountAdminSmall,
    ApiIcon,
    CguIcon,
    ChangeRole,
    English,
    French,
    HelpCenterSmall,
    LogOutIcon,
    MyTicketIcon,
    UsageGuideIcon
} from "@components/common/NdIcons/SvgCollection"

import {TenantUser} from "src/_generated/typed-document-nodes"
import bg from "src/assets/images/fondall.jpg"
import freedz from "src/assets/images/freedz.png"
import {
    CGU_URL,
    COOKIES_URL,
    DOC_API_URL,
    LEGAL_TERMS_URL,
    SUPPORT_URL,
    USER_GUIDE_URL
} from "src/constants/external-urls"
import {localStorageItemName} from "src/constants/local-storage-item-name"
import ErrorContext from "src/context/ErrorContext"
import {DropDownMenuProps} from "src/utils/@types/DropdownInterface"
import CommonUtils from "src/utils/Common"
import i18n from "src/utils/i18n"
import {readMainConfigValue} from "src/utils/mainConfig"
import {getAllNatures} from "src/utils/Queries/common"
import {initialTenantUser} from "src/utils/Queries/tenantUsers"
import Routing from "src/utils/Routing"
import {useApiFetch} from "src/utils/useApiFetch"

const drawerWidth = 240

/**
 * Mixins for the opened state of the drawer.
 *
 * @param {Theme} theme - The theme object from Material-UI.
 * @returns {CSSObject} - The CSS styles for the opened state of the drawer.
 */
const openedMixin = (theme: Theme): CSSObject => ({
    width: drawerWidth,
    transition: theme.transitions.create("width", {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen
    }),
    overflowX: "hidden"
})

/**
 * Mixins for the closed state of the drawer.
 *
 * @param {Theme} theme - The theme object from Material-UI.
 * @returns {CSSObject} - The CSS styles for the closed state of the drawer.
 */
const closedMixin = (theme: Theme): CSSObject => ({
    transition: theme.transitions.create("width", {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen
    }),
    overflowX: "hidden",
    width: `calc(${theme.spacing(7)} + 1px)`,
    [theme.breakpoints.up("sm")]: {
        width: `calc(${theme.spacing(8)} + 1px)`
    }
})

/**
 * Styled component for the drawer.
 *
 * @param {object} props - The props for the styled component.
 * @param {boolean} props.open - Indicates whether the drawer is open or closed.
 * @returns {JSX.Element} - The styled drawer component.
 */
const Drawer = styled(MuiDrawer, {
    shouldForwardProp: (prop) => prop !== "open"
})(({theme, open}) => ({
    width: drawerWidth,
    flexShrink: 0,
    whiteSpace: "nowrap",
    boxSizing: "border-box",
    ...(open && {
        ...openedMixin(theme),
        "& .MuiDrawer-paper": openedMixin(theme)
    }),
    ...(!open && {
        ...closedMixin(theme),
        "& .MuiDrawer-paper": closedMixin(theme)
    })
}))

/**
 * Styled component for the drawer header.
 *
 * @param {object} theme - The theme object from Material-UI.
 * @returns {CSSObject} - The CSS styles for the drawer header.
 */
const DrawerHeader = styled("div")(({theme}) => ({
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(0, 1),
    minHeight: "128px",
    margin: "10px 0",
    ...theme.mixins.toolbar
}))

function Sidebar() {
    const {sendApiRequest} = useApiFetch()
    const {t} = useTranslation("common")
    const context = useContext(ErrorContext)
    const {logout} = useKeycloakLogout()
    const [open] = React.useState(true)
    const [currentTenentUser, setCurrentTenentUser] = React.useState<TenantUser>(initialTenantUser)
    const location = useLocation()
    const navigate = useNavigate()

    useEffect(() => {
        const locacalUser: TenantUser = CommonUtils.GetLocal(localStorageItemName.tenantUser)
            ? JSON.parse(CommonUtils.GetLocal(localStorageItemName.tenantUser))
            : initialTenantUser
        if (currentTenentUser.email !== locacalUser.email) {
            setCurrentTenentUser(locacalUser)
        }
    }, [])

    const languageMenu = {
        button: (
            <>
                {CommonUtils.GetFullLanguage(CommonUtils.GetLocal(localStorageItemName.language)) === "Français" ? (
                    <French />
                ) : (
                    <English style={{width: 18, height: 12}} />
                )}
                <span className="normalCase" style={{fontSize: 10, marginLeft: 5}}>
                    {CommonUtils.GetFullLanguage(CommonUtils.GetLocal(localStorageItemName.language))}
                </span>
            </>
        ),
        items: [
            {
                key: "en",
                text: t("common:English"),
                click: () => changeLanguage("en")
            },
            {
                key: "fr",
                text: t("common:French"),
                click: () => changeLanguage("fr")
            }
        ]
    }

    const openNewTab = (url: string) => url && window.open(url, "_blank")

    const selectedRole = JSON.parse(CommonUtils.GetLocal(localStorageItemName.selectedRole))
    const selectedProduct = CommonUtils.GetLocal(localStorageItemName.selectedProduct)
    const parentDomainName = CommonUtils.getDomain(CommonUtils.getDomain(selectedRole?.domainId)?.parentDomainId)?.name
    const accountName = selectedProduct === "da" ? selectedRole?.domainName : selectedRole?.tenantName
    const accountBadge = selectedProduct === "da" ? parentDomainName : selectedRole?.domainName
    const showAccountBadge =
        selectedProduct === "nd" ||
        (selectedProduct === "da" && Boolean(CommonUtils.getDomain(selectedRole?.domainId)?.parentDomainId))

    const accountMenu: DropDownMenuProps = {
        topCard: (
            <Card className="generalTopCard accountTopCard">
                <div
                    style={{
                        display: "flex",
                        justifyContent: "flex-start",
                        alignItems: "center"
                    }}
                >
                    <Avatar
                        style={{
                            background: "#F5FAFE",
                            color: "#307ADE",
                            width: 50,
                            height: 50,
                            fontSize: 20,
                            borderColor: "rgba(48, 123, 222, 0.1)",
                            borderWidth: 2,
                            borderStyle: "solid",
                            marginRight: 10
                        }}
                        className="poppinsLightFont"
                    >
                        {currentTenentUser.email.replace(".", "").substring(0, 2).toUpperCase()}
                    </Avatar>
                    <div className="accountTopCardText">
                        <span style={{fontSize: 14}} title={currentTenentUser.firstName ?? undefined}>
                            {currentTenentUser.firstName}
                        </span>
                        <span
                            style={{color: "rgba(0, 0, 0, 0.7)", fontSize: 10}}
                            title={selectedProduct === "nd" ? selectedRole?.tenantName : selectedRole?.domainName}
                        >
                            {selectedProduct === "nd" ? selectedRole?.tenantName : selectedRole?.domainName}
                        </span>
                    </div>
                </div>
            </Card>
        ),
        bottomCard: (
            <Card className="generalTopCard accountBottomCard">
                <Grid
                    container
                    className="top7"
                    style={{
                        display: "flex",
                        justifyContent: "space-between",
                        marginBottom: 8
                    }}
                >
                    <DropDownMenuLight {...languageMenu} />
                    <Button
                        className="logOutBtn"
                        onClick={() => {
                            context.setGlobalLoading(true)
                            logOut(() => {
                                logout()
                            })
                        }}
                    >
                        <LogOutIcon style={{marginRight: 5}} />
                        {t("common:Logout")}
                    </Button>
                </Grid>
                <Grid container className="accountLinks">
                    <Grid size={2}>
                        <a href={CGU_URL} target="_blank" rel="noreferrer">
                            {t("common:CGU")}
                        </a>
                    </Grid>
                    <Grid size={3}>
                        <a href={COOKIES_URL} target="_blank" rel="noreferrer">
                            {t("common:Cookies")}
                        </a>
                    </Grid>
                    <Grid size={4}>
                        <div style={{fontSize: 10}}>{`v${import.meta.env.VITE_BUILD_VERSION || __APP_VERSION__}`}</div>
                    </Grid>
                </Grid>
            </Card>
        ),
        button: (
            <div className="UserAccount">
                <Avatar
                    style={{
                        background: "#F5FAFE",
                        color: "#307ADE",
                        width: 50,
                        height: 50,
                        fontSize: 20,
                        borderColor: "rgba(48, 123, 222, 0.1)",
                        borderWidth: 2,
                        borderStyle: "solid"
                    }}
                    className="poppinsLightFont"
                >
                    {currentTenentUser.email.replace(".", "").substring(0, 2).toUpperCase()}
                </Avatar>

                <div className="UserInfo">
                    <div className="userInfoName" title={accountName ?? undefined}>
                        {accountName}
                    </div>
                    {showAccountBadge && (
                        <div style={{display: "flex", minWidth: 0}}>
                            <div className="userInfoDomainWrapper">
                                <span className="userInfoDomain" title={accountBadge ?? undefined}>
                                    {accountBadge}
                                </span>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        ),
        items: [
            {
                key: "HelpCenter",
                icon: <HelpCenterSmall />,
                subItem: [
                    {
                        key: "myTickets",
                        text: t("common:MyTickets"),
                        icon: <MyTicketIcon />,
                        click: () => openNewTab(readMainConfigValue("Link_CustomerSupport") ?? SUPPORT_URL)
                    },
                    {
                        key: "userGuide",
                        icon: <UsageGuideIcon />,
                        text: t("common:UserGuide"),
                        click: () => openNewTab(USER_GUIDE_URL)
                    },
                    {
                        key: "docApi",
                        icon: <ApiIcon />,
                        text: t("common:DocApi"),
                        click: () => openNewTab(DOC_API_URL)
                    },
                    {
                        key: "legalTerms",
                        icon: <CguIcon />,
                        text: t("common:LegalTerms"),
                        click: () => openNewTab(LEGAL_TERMS_URL)
                    }
                ],
                text: t("common:HelpCenter")
            }
        ]
    }

    const isAutoload = localStorage.getItem(localStorageItemName.isAutoLogin)
        ? localStorage.getItem(localStorageItemName.isAutoLogin)
        : "false"

    if (JSON.stringify(isAutoload) === '"false"') {
        accountMenu.items.push({
            key: "changeRole",
            icon: <ChangeRole />,
            text: t("common:ChangeRole"),
            click: () => changeRole(),
            subItem: undefined
        })
    }

    if (selectedProduct === "nd") {
        accountMenu.items.splice(1, 0, {
            key: "accountSettings",
            icon: <AccountAdminSmall />,
            text: t("common:AccountSettings"),
            subItem: undefined,
            click: () => navigate(Routing.GetUrl("Administration").replace(":selectedTab", "0"))
        })
    }

    function FreedzLogo() {
        return <img alt="freedz" src={freedz} style={{width: 21, height: 21}} />
    }

    if (!["preprod-app.docoon.com", "app.docoon.com"].includes(window.location.host)) {
        accountMenu.items.push({
            key: "changeRole",
            icon: <FreedzLogo />,
            text: t("common:freedz"),
            click: () => (window.location.href = "https://portal.docoon.com/#/auth"),
            subItem: undefined
        })
    }

    function changeRole() {
        ChaneRoleBoradCast(() => {
            localStorage.setItem(localStorageItemName.isRoleSelected, "false")
            localStorage.setItem(localStorageItemName.selectedProduct, "")
            window.location.href = "/"
        })
    }

    async function changeLanguage(lng: string) {
        localStorage.setItem(localStorageItemName.language, lng)
        i18n.changeLanguage(lng)

        try {
            const naturesList: any = await sendApiRequest({
                url: CommonUtils.GetSetting("BUSINESS_API_URL"),
                query: getAllNatures,
                variables: {
                    params: {
                        sortField: "LABEL",
                        sortDirection: "ASCENDING"
                    },
                    after: "",
                    language: lng
                },
                odmToken: selectedRole?.token,
                type: "query",
                cache: context.cache,
                restore: true
            })
            localStorage.setItem(
                localStorageItemName.listNatures,
                JSON.stringify(naturesList.data.natureList.edges.filter((item) => item.code !== "ChorusB2G"))
            )
            window.location.reload()
        } catch (error) {
            console.error("Failed to change language:", error)
        }
    }

    const isRole = React.useMemo(
        () => CommonUtils.ToBoolean(CommonUtils.GetLocal(localStorageItemName.isRoleSelected)),
        []
    )

    const dynamicMenu = useCallback(
        (isRole2: boolean) => {
            return isRole ? Routing.CreateMenu(isRole2) : null
        },
        [isRole]
    )

    return (
        <>
            {!Routing.IsOffline(location.pathname) && isRole && (
                <Box sx={{display: "flex"}}>
                    <CssBaseline />
                    <Drawer
                        variant="permanent"
                        className="sideMenuMenu"
                        open={open}
                        sx={{
                            display: {xs: "none", sm: "block"},
                            "& .MuiDrawer-paper": {
                                backgroundImage: `url(${bg})`,
                                backgroundSize: "cover",
                                backgroundPosition: "center center"
                            }
                        }}
                    >
                        <DrawerHeader style={{margin: "10px 20px", padding: 0}}>
                            <Grid
                                style={{cursor: "pointer"}}
                                className="FullParent"
                                onClick={() => (window.location.href = "/")}
                            >
                                <img src="/logo.png" alt="logo" style={{width: "100%", margin: 0}} />
                            </Grid>
                        </DrawerHeader>
                        {dynamicMenu(isRole)}
                        {isRole && (
                            <Grid className="accountIcon">
                                <DropDownMenu {...accountMenu} />
                            </Grid>
                        )}
                    </Drawer>
                </Box>
            )}
        </>
    )
}

export default Sidebar
